/* Small data hooks shared by the catalogue.
   CONTRACT — owned by the foundation. See PLAN.md. */

import { useEffect, useState } from "react";
import { loadDoc, loadHandout, loadIndex } from "./content";
import type { Doc, IndexEntry } from "./types";
import type { Handout } from "./handout";

export type Async<T> = { data: T | null; error: Error | null; loading: boolean };

function useAsync<T>(run: () => Promise<T>, deps: unknown[]): Async<T> {
  const [state, setState] = useState<Async<T>>({ data: null, error: null, loading: true });
  useEffect(() => {
    /* A route change mid-flight must not let the abandoned answer overwrite the
       one that replaced it. */
    let live = true;
    setState({ data: null, error: null, loading: true });
    run().then(
      (data) => { if (live) setState({ data, error: null, loading: false }); },
      (error: Error) => { if (live) setState({ data: null, error, loading: false }); },
    );
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

/* The index is small and asked for on nearly every page, so it is fetched once
   per session and kept.

   Kept only when it arrives. A memo that holds a rejected promise remembers the
   failure as firmly as it would have remembered the answer: one refused request
   on a school network and the catalogue is empty for the rest of the session,
   in every tab of the portal, with a reload the only way out and nothing on the
   page saying so. Clearing it on the way through means the next page asks
   again — which on a network that dropped one request is usually all it takes. */
let indexOnce: Promise<IndexEntry[]> | null = null;
export function useIndex(): Async<IndexEntry[]> {
  return useAsync(() => (indexOnce ??= loadIndex().catch((e: unknown) => {
    indexOnce = null;
    throw e;
  })), []);
}

export function useDoc(id: string | undefined): Async<Doc> {
  return useAsync(() => (id ? loadDoc(id) : Promise.reject(new Error("no id"))), [id]);
}

export function useHandout(id: string | undefined): Async<Handout> {
  return useAsync(() => (id ? loadHandout(id) : Promise.reject(new Error("no id"))), [id]);
}

/* Several documents at once, keyed by id — a handout quotes more than one and
   must not render until every one of them is in. */
export function useDocs(ids: string[]): Async<Record<string, Doc>> {
  const key = ids.join(",");
  return useAsync(async () => {
    const loaded = await Promise.all(ids.map((id) => loadDoc(id)));
    return Object.fromEntries(loaded.map((d, i) => [ids[i]!, d]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/* ---------- catalogue queries ----------

   Plain functions over the index. The hierarchy is strand -> subject -> year ->
   material, which is the order teachers were observed working in. */

export const inStrand = (rows: IndexEntry[], strand: string) =>
  rows.filter((e) => e.strand.includes(strand));

export function subjectsIn(rows: IndexEntry[], strand: string, order: string[]) {
  const count = new Map<string, number>();
  for (const e of inStrand(rows, strand)) count.set(e.subject, (count.get(e.subject) ?? 0) + 1);
  return order.filter((s) => count.has(s)).map((s) => ({ id: s, count: count.get(s)! }));
}

export function yearsIn(rows: IndexEntry[], strand: string, subject: string, years: number[]) {
  const count = new Map<number, number>();
  for (const e of inStrand(rows, strand)) {
    if (e.subject === subject) count.set(e.year, (count.get(e.year) ?? 0) + 1);
  }
  /* A year with nothing behind it gets no tile: an empty row reads as a broken
     page, a missing one reads as a subject that starts later. */
  return years.filter((y) => count.has(y)).map((y) => ({ year: y, count: count.get(y)! }));
}

export const itemsIn = (rows: IndexEntry[], strand: string, subject: string, year?: number) =>
  inStrand(rows, strand).filter((e) => e.subject === subject && (year == null || e.year === year));
